import * as React from 'react'

import { cn } from '../../lib/utils'
import { Label } from './label'

/**
 * Lightweight replacement for Joy's <FormControl> + <FormLabel> +
 * <FormHelperText> trio. Migrated call sites can swap directly to:
 *
 *   <Field data-invalid={Boolean(error) || undefined}>
 *     <FieldLabel htmlFor="name" required>Name</FieldLabel>
 *     <Input id="name" ... />
 *     <FieldHelp>What we'll address you as.</FieldHelp>
 *     <FieldError>Required</FieldError>
 *   </Field>
 *
 * `data-invalid` cascade
 * ----------------------
 * Setting `data-invalid` on the Field wrapper now reaches descendant Input
 * and Textarea primitives automatically — they declare
 * `group-data-[invalid=true]/field:border-destructive ...` variants that
 * latch onto this `group/field` ancestor. Pre-fix, only `aria-invalid` on
 * the input itself would trigger the destructive border, but every maestro
 * form sets `data-invalid` on the Field wrapper instead. Result: the red
 * invalid border never appeared. Adding `group/field` here flips every
 * existing `data-invalid={Boolean(errors.x)}` callsite from broken to
 * working without further changes.
 *
 * `required` on FieldLabel
 * ------------------------
 * Pass `required` to render a destructive-coloured `*` after the label text
 * — matches the pattern ontology-ui uses on its local Field components and
 * is what users expect from any form with required fields. Hidden from
 * screen readers (`aria-hidden`) because RHF / zod will surface the real
 * required-ness via aria-invalid + error text instead.
 *
 * Every primitive in this file is forwardRef so it can be the target of a
 * react-hook-form Controller ref, a Radix Slot asChild ref, or anything else
 * that needs the underlying DOM handle under React 18.
 */

interface FieldProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'orientation'> {
  /**
   * `vertical` (default) — label on top, input below. Standard form layout.
   * `horizontal` — label group on the left, control on the right (useful
   * for switch / checkbox rows where the control sits inline with its label).
   */
  orientation?: 'vertical' | 'horizontal'
}

const Field = React.forwardRef<HTMLDivElement, FieldProps>(function Field(
  { className, orientation = 'vertical', ...props },
  ref
) {
  return (
    <div
      ref={ref}
      data-slot="field"
      data-orientation={orientation}
      className={cn(
        'group/field flex gap-1.5',
        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
        className
      )}
      {...props}
    />
  )
})

interface FieldLabelProps extends React.ComponentPropsWithoutRef<typeof Label> {
  /**
   * Renders a destructive-coloured `*` after the label text. Decorative
   * (`aria-hidden`) — actual required-ness should flow through `aria-invalid`
   * + error text so screen readers announce it semantically.
   */
  required?: boolean
}

const FieldLabel = React.forwardRef<React.ElementRef<typeof Label>, FieldLabelProps>(
  function FieldLabel({ className, required, children, ...props }, ref) {
    // The underlying Label primitive is `flex items-center gap-2`, so if we
    // rendered `{children}` and the asterisk as direct siblings the `*`
    // would sit ~10px away from the label text. Wrap them in a single span
    // child so the label string and the `*` group tightly (ml-0.5) inside
    // the Label's flex layout.
    return (
      <Label
        ref={ref}
        data-slot="field-label"
        className={cn('text-sm font-medium', className)}
        {...props}
      >
        <span>
          {children}
          {required && (
            <span aria-hidden className="ml-0.5 text-destructive">
              *
            </span>
          )}
        </span>
      </Label>
    )
  }
)

const FieldHelp = React.forwardRef<HTMLParagraphElement, React.ComponentPropsWithoutRef<'p'>>(
  function FieldHelp({ className, ...props }, ref) {
    return (
      <p
        ref={ref}
        data-slot="field-help"
        // FieldHelp doubles as both *help text* (descriptive hints when the
        // field is valid) and *error text* (RHF error messages rendered
        // conditionally when invalid). Pre-fix, both cases rendered muted-
        // grey, so error messages were visually indistinguishable from
        // hints. The `group-data-[invalid=true]/field:` variant flips it to
        // destructive automatically whenever the wrapping Field has
        // `data-invalid` — which is exactly when our forms conditionally
        // render FieldHelp with an error message. No callsite changes
        // needed, and `<FieldError>` is still available for explicit
        // error-only rendering.
        className={cn(
          'text-muted-foreground text-xs',
          'group-data-[invalid=true]/field:text-destructive',
          className
        )}
        {...props}
      />
    )
  }
)

const FieldError = React.forwardRef<HTMLParagraphElement, React.ComponentPropsWithoutRef<'p'>>(
  function FieldError({ className, ...props }, ref) {
    return (
      <p
        ref={ref}
        data-slot="field-error"
        role="alert"
        className={cn('text-destructive text-xs', className)}
        {...props}
      />
    )
  }
)

/**
 * Descriptive helper text for a field — the explanatory blurb that sits
 * below a label, typically next to a Switch / Checkbox / RadioGroup row
 * (e.g. "If enabled, existing projects with the same name will be
 * overwritten."). Distinct from `FieldHelp` which doubles as error text
 * via the `data-invalid` cascade; `FieldDescription` is purely descriptive
 * and stays muted regardless of validation state.
 */
const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(function FieldDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      data-slot="field-description"
      className={cn('text-muted-foreground text-xs', className)}
      {...props}
    />
  )
})

export { Field, FieldLabel, FieldHelp, FieldError, FieldDescription }
